import { Component, input, OnDestroy, OnInit, output, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from 'src/components/button/button.component';
import { IconModule } from 'src/components/icon/icon.module';
import { Form, FormError } from 'src/definitions/form.definition';
import { ForgotPinSetNewForm } from 'src/definitions/auth.definition';
import { ConfirmPinValidator, FormService } from 'src/services/form.service';
import { GlobalService } from 'src/services/global.service';
import { RestService } from 'src/services/rest.service';
import { CommonModule } from '@angular/common';
import { lastValueFrom } from 'rxjs';
import { InputOtpModule } from 'primeng/inputotp';
import { AutoFocusModule } from 'primeng/autofocus';
import { ToastService } from 'src/services/toast.service';

@Component({
  selector: 'step-pin',
  templateUrl: './step-pin.component.html',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    InputOtpModule,
    AutoFocusModule,
    IconModule,
    ButtonComponent,
  ],
})
export class _StepPinComponent implements OnInit {
  token = input<string>('');
  email = input<string>('');

  toPrev = output<void>();
  toNext = output<void>();

  protected step: 'default' | 'confirm' = 'default';

  protected pinLength = 6;
  protected pinType: 'text' | 'password' = 'password';

  protected form: Form<ForgotPinSetNewForm> = new FormBuilder().nonNullable.group(
    {
      email: ['', [Validators.required]],
      reset_pin_token: ['', [Validators.required]],
      pin: ['', [Validators.required]],
      pin_confirmation: ['', [Validators.required]],
    },
    { validators: [ConfirmPinValidator('pin', 'pin_confirmation')] },
  );

  protected formError: FormError<ForgotPinSetNewForm> = {
    label: '',
    msg: '',
    email: { label: 'E-mail', msg: '' },
    reset_pin_token: { label: 'Token', msg: '' },
    pin: { label: 'PIN', msg: '' },
    pin_confirmation: { label: 'Konfirmasi PIN', msg: '' },
  };

  protected formSubmitting = signal<boolean>(false);

  constructor(
    private restService: RestService,
    private toastService: ToastService,
    protected formService: FormService,
    protected globalService: GlobalService,
  ) {}

  ngOnInit(): void {
    this.form.patchValue({
      email: this.email(),
      reset_pin_token: this.token(),
    });
  }
  protected _onPaste(event: ClipboardEvent, type: 'pin' | 'pin_confirmation') {
    const value = event.clipboardData?.getData('text');
    if (!Number(value)) return;
    this.form.controls[type].patchValue(value?.slice(0, this.pinLength) ?? '');
  }

  protected _togglePinType() {
    this.pinType = this.pinType === 'text' ? 'password' : 'text';
  }

  protected _toStepConfirm() {
    this.step = 'confirm';
    this.pinType = 'password';
  }

  protected async _onSubmit() {
    if (this.formSubmitting()) return;

    const invalid = this.formService.validateAndGetErrorMessage(this.form, this.formError);
    if (invalid) {
      this.globalService.errorHandler(new Error(invalid), 'Gagal meminta kode OTP');
      return;
    }

    try {
      this.formSubmitting.set(true);

      const payload = { ...this.form.getRawValue() };
      await lastValueFrom(this.restService.forgotPinSetNew(payload));

      this.toastService.showToast({
        summary: 'Berhasil',
        detail: 'Kode OTP berhasil dikirimkan ke email kamu.',
        severity: 'success',
      });

      this.toNext.emit();
    } catch (error) {
      error = this.formService.serverErrorHandler(error, this.formError);
      this.globalService.errorHandler(error, 'Gagal meminta kode OTP');
    } finally {
      this.formSubmitting.set(false);
    }
  }

  protected _back() {
    if (this.step === 'confirm') {
      this.step = 'default';
      return;
    }
    this.toPrev.emit();
  }
}
